"use client";

import React from "react";
import SumsubWebSdk from "@sumsub/websdk-react";
import { motion } from "framer-motion";
import { useSumSub, SumsubMessage } from "../hooks/use-sumsub";
import { useCardStatusStore } from "../stores/cardStatusStore";
import { useKycStore } from "../stores/useKycStore";
import { SDKWrapper } from "./SDKWrapper";

interface SumSubWidgetProps {
  eoaAddress?: string;
  onApplicantIdChange?: (id: string) => void;
  onVerificationComplete?: () => void;
}

const LoadingBar: React.FC<{ className?: string }> = ({ className = "" }) => {
  return (
    <div
      className={`h-4 overflow-hidden bg-white relative w-full ${className}`}
    >
      <motion.div
        className="bg-red-500 h-full absolute top-0 left-0"
        initial={{ width: "0%" }}
        animate={{ width: "100%" }}
        transition={{
          duration: 2,
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop",
        }}
      />
    </div>
  );
};
interface RetroButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "primary";
}

const RetroButton: React.FC<RetroButtonProps> = ({
  children,
  variant = "default",
  className = "",
  ...props
}) => {
  const variantClasses = {
    default: "bg-[#E6BE8A] text-[#3B2F2F] border-[#8B4513] hover:bg-[#E6BE8A]",
    primary: "bg-[#8B4513] text-[#FFD700] border-[#D4AF37] hover:bg-[#8B4513]",
  };

  return (
    <button
      className={`
        font-['Times_New_Roman',_Times,_serif] 
        px-4 py-1.5 text-base
        ${variantClasses[variant]}
        ${className}
        border-2
        active:translate-y-[1px]
        flex items-center justify-center gap-2
        transition-none
        disabled:opacity-50 disabled:cursor-not-allowed
      `}
      {...props}
    >
      <span>{children}</span>
    </button>
  );
};

export function SumSubWidget({
  eoaAddress,
  onApplicantIdChange,
  onVerificationComplete,
}: SumSubWidgetProps) {
  const {
    accessToken,
    isLoading,
    error,
    handleMessage,
    handleError,
    handleTokenExpiration,
    retry,
  } = useSumSub({
    eoaAddress,
    onApplicantIdChange,
    onVerificationComplete,
  });

  const { _setUserStatus } = useCardStatusStore();
  const { setIsSumsubProfileVerified, isSumsubProfileVerified } = useKycStore();

  if (isLoading) {
    return (
      <SDKWrapper>
        <div className="text-center p-6 min-h-[30vh] flex flex-col justify-center">
          <p className="font-['MS_Sans_Serif',_Arial,_sans-serif] mb-4 text-white">
            Loading SumSub verification module...
          </p>
          <LoadingBar className="w-[50rem] mx-auto" />
        </div>
      </SDKWrapper>
    );
  }

  if (error || !accessToken) {
    return (
      <SDKWrapper>
        <div className="text-center p-6 min-h-[10vh] flex flex-col justify-center">
          <p className="font-['MS_Sans_Serif',_Arial,_sans-serif] mb-4 text-red-600">
            {error || "Failed to load verification module. Please try again."}
          </p>
          <RetroButton variant="primary" onClick={retry}>
            Try Again
          </RetroButton>
        </div>
      </SDKWrapper>
    );
  }

  const sdkKey = `sumsub-sdk-${accessToken}`;

  return (
    <SDKWrapper>
      <div className="relative rounded-md w-full min-h-[30vh]">
        <div className="relative w-full" style={{ minHeight: "400px" }}>
          <SumsubWebSdk
            key={sdkKey}
            accessToken={accessToken}
            expirationHandler={handleTokenExpiration}
            config={{
              lang: "en",
              customizationName: "swype",
              theme: "dark",
            }}
            options={{
              addViewportTag: false,
              adaptIframeHeight: true,
            }}
            onMessage={(message: SumsubMessage, payload: any) => {
              console.log("Sumsub onMessage:", message.type, payload);
              if (payload && payload.reviewStatus === "completed") {
                _setUserStatus("initiated");
                setIsSumsubProfileVerified(true);
              }
              handleMessage(message);
            }}
            onError={handleError}
          />
        </div>

        {isSumsubProfileVerified && (
          <div className="w-full mt-4">
            <button
              disabled={!isSumsubProfileVerified}
              className={`flex h-10 w-full justify-center items-center gap-1 self-stretch rounded-lg bg-white backdrop-blur-[6px] text-black font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100`}
              onClick={() => _setUserStatus("pending")}
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </SDKWrapper>
  );
}
